import { useEffect, useRef, useState } from "react";
import { TrendUp, TrendDown, Minus, ArrowClockwise, WarningCircle } from "@phosphor-icons/react";

export function RetryState({ label = "Couldn't load data.", onRetry }) {
  return (
    <div data-testid="retry-state" className="flex flex-col items-center justify-center py-24 text-center">
      <WarningCircle size={34} className="text-signal-yellow" weight="fill" />
      <p className="mt-3 text-sm text-muted-foreground max-w-sm">{label}</p>
      <button data-testid="retry-btn" onClick={onRetry}
        className="mt-4 inline-flex items-center gap-2 bg-volt hover:bg-volt/90 text-white rounded-sm px-4 py-2 text-sm transition-colors duration-200">
        <ArrowClockwise size={15} /> Retry
      </button>
    </div>
  );
}

const REGIME_STYLE = {
  STRONG_BULL: "bg-signal-green/20 text-signal-green border-signal-green/40",
  BULL: "bg-signal-green/10 text-signal-green border-signal-green/25",
  BULL_VOLATILE: "bg-signal-yellow/20 text-signal-yellow border-signal-yellow/40",
  NEUTRAL: "bg-white/10 text-muted-foreground border-white/15",
  BEAR_VOLATILE: "bg-orange-500/20 text-orange-400 border-orange-500/40",
  BEAR: "bg-signal-red/10 text-signal-red border-signal-red/25",
  RISK_OFF: "bg-signal-red/25 text-signal-red border-signal-red/50",
};

export function RegimeBadge({ regime, className = "" }) {
  const style = REGIME_STYLE[regime] || REGIME_STYLE.NEUTRAL;
  return (
    <span data-testid="market-regime-indicator"
      className={`inline-flex items-center gap-1.5 border px-2.5 py-1 text-[11px] font-semibold tracking-wide rounded-sm mono ${style} ${className}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current animate-pulse" />
      {regime?.replace(/_/g, " ")}
    </span>
  );
}

// Classification tag per probabilistic visual language
const KIND = {
  FACT: { label: "FACT", cls: "text-white border-white/40" },
  MODEL: { label: "MODEL OUTPUT", cls: "text-muted-foreground border-dashed border-white/30" },
  AI: { label: "AI INTERPRETATION", cls: "text-volt border-volt/40 ai-glow" },
  HYPO: { label: "HYPOTHETICAL", cls: "text-signal-yellow border-signal-yellow/40 italic" },
  REAL: { label: "REAL MONEY", cls: "text-signal-green border-signal-green/40" },
};
export function DataTag({ kind }) {
  const k = KIND[kind] || KIND.FACT;
  return <span className={`inline-block border px-1.5 py-0.5 text-[9px] uppercase tracking-[0.18em] rounded-sm mono ${k.cls}`}>{k.label}</span>;
}

export function ScoreGauge({ score = 50, direction = "neutral", size = 130 }) {
  const r = size / 2 - 10;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, score));
  const offset = c - (pct / 100) * c;
  const color = direction === "bullish" ? "#34C759" : direction === "bearish" ? "#FF3B30" : "#007AFF";
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }} data-testid="ai-score-gauge">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(255,255,255,0.08)" strokeWidth="8" fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth="8" fill="none"
          strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.8s ease", filter: `drop-shadow(0 0 6px ${color}88)` }} />
      </svg>
      <div className="absolute flex flex-col items-center">
        <span className="mono text-4xl font-semibold ai-glow" style={{ color }}>{score}</span>
        <span className="label-xs mt-0.5">AI Score</span>
      </div>
    </div>
  );
}

export function ProbBar({ bull, neutral, bear }) {
  const b = Math.round(bull * 100), n = Math.round(neutral * 100), r = Math.round(bear * 100);
  return (
    <div data-testid="prob-distribution">
      <div className="flex h-3 w-full overflow-hidden rounded-sm border border-white/10 border-dashed">
        <div className="bg-signal-green/70" style={{ width: `${b}%` }} title={`Bull ${b}%`} />
        <div className="bg-white/15" style={{ width: `${n}%` }} title={`Neutral ${n}%`} />
        <div className="bg-signal-red/70" style={{ width: `${r}%` }} title={`Bear ${r}%`} />
      </div>
      <div className="mt-1.5 flex justify-between mono text-[11px]">
        <span className="pos">↑ Bull {b}%</span>
        <span className="text-muted-foreground">Neutral {n}%</span>
        <span className="neg">↓ Bear {r}%</span>
      </div>
    </div>
  );
}

export function Change({ value, className = "", suffix = "%" }) {
  const up = value >= 0;
  return (
    <span className={`mono inline-flex items-center gap-0.5 ${up ? "pos" : "neg"} ${className}`}>
      {up ? <TrendUp size={14} weight="bold" /> : <TrendDown size={14} weight="bold" />}
      {up ? "+" : ""}{value?.toFixed(2)}{suffix}
    </span>
  );
}

export function Stat({ label, value, sub, mono = true, valueClass = "" }) {
  return (
    <div className="border border-border bg-card p-4">
      <div className="label-xs">{label}</div>
      <div className={`mt-1.5 text-xl ${mono ? "mono" : ""} font-semibold ${valueClass}`}>{value}</div>
      {sub && <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

export function Loader({ label = "Loading market intelligence" }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-muted-foreground">
      <div className="mono text-xs tracking-widest animate-pulse">{label}...</div>
      <div className="mt-3 h-px w-40 overflow-hidden bg-white/10">
        <div className="h-full w-1/3 bg-volt animate-[loading_1.2s_ease-in-out_infinite]" />
      </div>
      <style>{`@keyframes loading{0%{transform:translateX(-100%)}100%{transform:translateX(400%)}}`}</style>
    </div>
  );
}

export function Empty({ children }) {
  return <div className="mono text-xs text-muted-foreground py-12 text-center tracking-wide">{children}</div>;
}

export function LiveBadge({ connected }) {
  return (
    <span className="inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-[0.18em]" data-testid="live-badge">
      <span className={`h-1.5 w-1.5 rounded-full ${connected ? "bg-signal-green animate-pulse" : "bg-muted-foreground/50"}`} />
      <span className={connected ? "text-signal-green" : "text-muted-foreground"}>{connected ? "Live" : "Offline"}</span>
    </span>
  );
}

// Price cell that flashes green/red when the value updates
export function LivePrice({ value, className = "" }) {
  const [flash, setFlash] = useState("");
  const prev = useRef(value);
  useEffect(() => {
    if (value != null && prev.current != null && value !== prev.current) {
      setFlash(value > prev.current ? "flash-up" : "flash-down");
      const t = setTimeout(() => setFlash(""), 700);
      prev.current = value;
      return () => clearTimeout(t);
    }
    prev.current = value;
  }, [value]);
  return <span className={`mono transition-colors duration-500 ${flash === "flash-up" ? "pos" : flash === "flash-down" ? "neg" : ""} ${className}`}>${value}</span>;
}

// Quick-size chips for order tickets: fixed $ amounts + optional % of buying power.
// onPick receives a dollar amount. `cash` (buying power) enables the % chips.
export function AmountChips({ cash, onPick, testid = "amt-chips", maxLabel = "Max" }) {
  const fixed = [100, 500, 1000];
  const pcts = [["25%", 0.25], ["50%", 0.5], [maxLabel, 1]];
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {fixed.map((v) => (
        <button key={v} type="button" data-testid={`${testid}-${v}`} onClick={() => onPick(v)}
          className="mono text-[10px] px-2 py-1 rounded-sm border border-border text-muted-foreground hover:border-volt/50 hover:text-volt transition-colors duration-150">
          ${v >= 1000 ? `${v / 1000}K` : v}
        </button>
      ))}
      {cash != null && cash > 0 && pcts.map(([label, f]) => (
        <button key={label} type="button" data-testid={`${testid}-${label}`} onClick={() => onPick(Math.floor(cash * f))}
          className="mono text-[10px] px-2 py-1 rounded-sm border border-volt/30 text-volt/90 hover:bg-volt/10 transition-colors duration-150">
          {label}
        </button>
      ))}
    </div>
  );
}
